import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Webhook, ArrowRight, RotateCw } from "lucide-react";

const EVENTS = [
  { id: "coupon.claimed", label: "Coupon claimed" },
  { id: "coupon.validated", label: "Coupon validated at checkout" },
  { id: "coupon.redeemed", label: "Coupon redeemed (order completed)" },
];

export default function MerchantWebhooks() {
  const [hooks, setHooks] = useState([]);
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState({ "coupon.claimed": true, "coupon.validated": true, "coupon.redeemed": true });
  const [newSecret, setNewSecret] = useState(null);
  const [deliveries, setDeliveries] = useState({ id: null, items: [] });
  const [replayingId, setReplayingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/merchant/webhooks");
    setHooks(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const events = Object.keys(selected).filter((k) => selected[k]);
    if (!events.length) return toast.error("Pick at least one event");
    try {
      const { data } = await api.post("/merchant/webhooks", { url, events });
      setNewSecret(data.secret);
      setUrl("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    await api.delete(`/merchant/webhooks/${id}`);
    load();
    if (deliveries.id === id) setDeliveries({ id: null, items: [] });
  };

  const showDeliveries = async (id) => {
    const { data } = await api.get(`/merchant/webhooks/${id}/deliveries`);
    setDeliveries({ id, items: data });
  };

  const replay = async (deliveryId) => {
    setReplayingId(deliveryId);
    try {
      const { data } = await api.post(`/merchant/webhooks/deliveries/${deliveryId}/replay`);
      if (data.status === "delivered") toast.success(`Replayed — HTTP ${data.response_code}`);
      else toast.error(`Replay failed — ${data.response_code || data.error || "no response"}`);
      if (deliveries.id) await showDeliveries(deliveries.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replay failed");
    } finally {
      setReplayingId(null);
    }
  };

  return (
    <div data-testid="merchant-webhooks">
      <div className="pf-chip pf-chip-lav">Realtime</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Webhooks</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        Get instant HTTP callbacks when Cyannect events happen. Payloads are HMAC-signed with your webhook secret and retried up to 5 times with exponential backoff.
      </p>

      {newSecret && (
        <div className="pf-card p-5 mt-5 bg-gradient-to-br from-cyan-50 to-emerald-50 border-cyan-200" data-testid="new-secret">
          <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold">Copy this signing secret — you won't see it again</div>
          <code className="block font-mono text-sm bg-white/80 px-3 py-2 rounded-lg mt-2 truncate">{newSecret}</code>
          <p className="text-xs text-slate-600 mt-2">Verify signatures on your endpoint by computing <code className="font-mono">HMAC-SHA256(secret, body)</code> and comparing to the <code className="font-mono">X-Cyannect-Signature</code> header.</p>
          <button onClick={() => setNewSecret(null)} className="mt-3 pf-btn-glass px-3 py-1.5 rounded-full text-xs">Dismiss</button>
        </div>
      )}

      <form onSubmit={create} className="pf-card p-5 mt-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Endpoint URL</label>
          <input required type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.your-shop.com/webhooks/cyannect" className="pf-input w-full px-3 py-2 mt-1 text-sm" data-testid="webhook-url" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Events</label>
          <div className="flex flex-wrap gap-2">
            {EVENTS.map((ev) => (
              <label key={ev.id} className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer border transition ${selected[ev.id] ? "bg-cyan-600 text-white border-cyan-600" : "bg-white/60 border-white/80 text-slate-600"}`}>
                <input type="checkbox" className="hidden" checked={!!selected[ev.id]} onChange={(e) => setSelected({ ...selected, [ev.id]: e.target.checked })} />
                {ev.label}
              </label>
            ))}
          </div>
        </div>
        <button type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="webhook-add">
          <Plus className="w-4 h-4" /> Add webhook
        </button>
      </form>

      <div className="pf-card overflow-hidden mt-6">
        <table className="w-full text-sm">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3">URL</th>
              <th className="px-5 py-3">Events</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {hooks.map((h) => (
              <tr key={h.id} className="border-t border-white/60" data-testid={`hook-row-${h.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-slate-800 truncate max-w-xs">{h.url}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(h.events || []).map((ev) => <span key={ev} className="pf-chip">{ev}</span>)}
                  </div>
                </td>
                <td className="px-5 py-3"><span className={`pf-chip ${h.enabled ? "pf-chip-mint" : ""}`}>{h.enabled ? "active" : "disabled"}</span></td>
                <td className="px-5 py-3 text-right space-x-1">
                  <button onClick={() => showDeliveries(h.id)} className="text-xs font-semibold px-3 py-1 rounded-full pf-btn-glass inline-flex items-center gap-1" data-testid={`hook-deliveries-${h.id}`}>Deliveries <ArrowRight className="w-3 h-3" /></button>
                  <button onClick={() => remove(h.id)} className="p-1.5 rounded hover:bg-white/70 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {hooks.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500"><Webhook className="w-6 h-6 mx-auto mb-2 text-cyan-600" />No webhooks yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {deliveries.id && (
        <div className="pf-card p-6 mt-6" data-testid="delivery-log">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Recent deliveries</div>
          <table className="w-full text-sm">
            <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2">Event</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Attempt</th><th className="px-3 py-2">HTTP</th><th className="px-3 py-2">When</th><th className="px-3 py-2 text-right"></th></tr>
            </thead>
            <tbody>
              {deliveries.items.map((d) => (
                <tr key={d.id} className="border-t border-white/60" data-testid={`delivery-row-${d.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {d.event}
                    {d.replayed_from && (
                      <span className="ml-2 pf-chip pf-chip-lav" title={`Manual replay of ${d.replayed_from}`}>replay</span>
                    )}
                  </td>
                  <td className="px-3 py-2"><span className={`pf-chip ${d.status === "delivered" ? "pf-chip-mint" : ""}`}>{d.status}</span></td>
                  <td className="px-3 py-2 font-mono text-xs">#{d.attempt}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.response_code || (d.error ? "err" : "—")}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{new Date(d.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">
                    {d.payload !== undefined && d.payload !== null ? (
                      <button
                        onClick={() => replay(d.id)}
                        disabled={replayingId === d.id}
                        className="text-xs font-semibold px-2.5 py-1 rounded-full pf-btn-glass inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`replay-delivery-${d.id}`}
                        title="Send this event again"
                      >
                        <RotateCw className={`w-3 h-3 ${replayingId === d.id ? "animate-spin" : ""}`} />
                        {replayingId === d.id ? "Sending" : "Replay"}
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {deliveries.items.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No deliveries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
